import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { type RespondedType, type ResponseError, responseScheme } from '@/models/response';
import ROUTES from '@/routes';
import { useAuthStore } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useSignUp() {
  const navigate = useNavigate();

  const mutation = useMutation<RespondedType & SignUpResponse, ResponseError, SignUpRequest>({
    mutationKey: ['/PhoneRegister'],
    mutationFn: (params) =>
      fetcher('/PhoneRegister', params, {
        requireAuth: false,
        schema: responseScheme.merge(signUpResponseSchema),
        validResponseCodes: [0],
      }),
    retry: false,
    onSuccess: (response) => {
      if (response.Code === 0 && response.UID) {
        useAuthStore.getState().start({
          UserID: response.UID,
          Token: response.Token || response.AccessToken || '',
          RefreshToken: response.RefreshToken || '',
        });
        navigate(ROUTES.MESSAGES.$path(), { replace: true });
      }
    },
    onSettled: (data, error) => {
      addToast({
        type: error ? 'error' : 'success',
        message: error?.message ?? data?.Msg,
      });
    },
  });

  const signUp = async (input: SignUpRequest) => mutation.mutateAsync(input).then((res) => res.Code === 0);

  return {
    signUp,
    ...mutation,
  };
}

const signUpRequestSchema = z.object({
  Phone: z.string().regex(/^\d+$/),
  TelCode: z.string(),
  Code: z.string().min(6),
});

type SignUpRequest = z.infer<typeof signUpRequestSchema>;

const signUpResponseSchema = z
  .object({
    UID: z.number().nullish(),
    Token: z.string().nullish(),
    AccessToken: z.string().nullish(),
    RefreshToken: z.string().nullish(),
  })
  .partial();

type SignUpResponse = z.infer<typeof signUpResponseSchema>;
